<template>
  <Transition name="settings-hub">
    <div
      v-if="visible"
      class="sh-backdrop"
      @click.self="emit('close')"
      @keydown.esc="emit('close')"
      role="dialog"
      aria-modal="true"
      :aria-label="STRINGS.aria.labels.settingsHub"
    >
      <div class="sh-panel">
        <!-- Header -->
        <div class="sh-header">
          <span class="sh-title font-display">{{ STRINGS.settingsHub.title }}</span>
          <button class="sh-close" @click="emit('close')" :aria-label="STRINGS.settingsHub.close">
            <AppIcon name="close-x" :size="16" />
          </button>
        </div>

        <div class="sh-body">
          <!-- Desktop sidebar nav -->
          <nav class="sh-sidebar" :aria-label="STRINGS.aria.labels.settingsNavigation">
            <div class="sh-nav-group" v-for="group in navGroups" :key="group.label">
              <span class="sh-nav-group-label">{{ group.label }}</span>
              <button
                v-for="item in group.items"
                :key="item.id"
                class="sh-nav-item"
                :class="{ 'sh-nav-item--active': activeSection === item.id }"
                @click="activeSection = item.id"
              >
                {{ item.label }}
              </button>
            </div>
          </nav>

          <!-- Mobile horizontal nav -->
          <nav class="sh-mobile-nav" :aria-label="STRINGS.aria.labels.settingsNavigation">
            <button
              v-for="item in flatNavItems"
              :key="item.id"
              class="sh-mobile-nav-item"
              :class="{ 'sh-mobile-nav-item--active': activeSection === item.id }"
              @click="activeSection = item.id"
            >
              {{ item.label }}
            </button>
          </nav>

          <!-- Content area -->
          <div class="sh-content">
            <h2 class="sh-section-title font-display">{{ sectionTitle }}</h2>

            <SettingsProfile v-if="activeSection === 'profile'" />
            <SettingsAccount v-else-if="activeSection === 'account'" />
            <SettingsRules v-else-if="activeSection === 'rules'" />
            <SettingsFeedback
              v-else-if="activeSection === 'feedback'"
              :visible="activeSection === 'feedback'"
            />
            <SettingsStaticContent v-else-if="activeSection === 'about'" page="about" />
            <SettingsStaticContent v-else-if="activeSection === 'privacy'" page="privacy" />
            <SettingsStaticContent v-else-if="activeSection === 'terms'" page="terms" />
            <SettingsStaticContent v-else-if="activeSection === 'cookies'" page="cookies" />
          </div>
        </div>

        <!-- Mobile logout footer -->
        <div class="sh-footer">
          <button class="sh-logout" :aria-label="STRINGS.aria.labels.logOut" @click="handleLogout">
            <i class="pi pi-sign-out" aria-hidden="true"></i>
            {{ STRINGS.aria.labels.logOut }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import AppIcon from './ui/icons/AppIcon.vue';
import SettingsProfile from './settings/SettingsProfile.vue';
import SettingsAccount from './settings/SettingsAccount.vue';
import SettingsRules from './settings/SettingsRules.vue';
import SettingsFeedback from './settings/SettingsFeedback.vue';
import SettingsStaticContent from './settings/SettingsStaticContent.vue';
import { useAuth } from '@/composables/useAuth';
import STRINGS from '@/constants/strings';

export type SettingsSection =
  | 'profile'
  | 'account'
  | 'rules'
  | 'feedback'
  | 'about'
  | 'privacy'
  | 'terms'
  | 'cookies';

interface Props {
  visible: boolean;
  initialSection?: SettingsSection;
}

const props = withDefaults(defineProps<Props>(), {
  initialSection: 'profile',
});

const emit = defineEmits<{
  close: [];
}>();

const auth = useAuth();

const activeSection = ref<SettingsSection>(props.initialSection);

interface NavItem {
  id: SettingsSection;
  label: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups = computed<NavGroup[]>(() => {
  const gameItems: NavItem[] = [{ id: 'rules', label: STRINGS.settingsHub.sections.rules }];
  if (auth.isEmailVerified.value) {
    gameItems.push({ id: 'feedback', label: STRINGS.settingsHub.sections.feedback });
  }
  return [
    {
      label: STRINGS.settingsHub.groups.account,
      items: [
        { id: 'profile', label: STRINGS.settingsHub.sections.profile },
        { id: 'account', label: STRINGS.settingsHub.sections.account },
      ],
    },
    {
      label: STRINGS.settingsHub.groups.game,
      items: gameItems,
    },
    {
      label: STRINGS.settingsHub.groups.info,
      items: [
        { id: 'about', label: STRINGS.settingsHub.sections.about },
        { id: 'privacy', label: STRINGS.settingsHub.sections.privacy },
        { id: 'terms', label: STRINGS.settingsHub.sections.terms },
        { id: 'cookies', label: STRINGS.settingsHub.sections.cookies },
      ],
    },
  ];
});

const flatNavItems = computed(() => navGroups.value.flatMap(g => g.items));

const sectionTitle = computed(() => {
  const item = flatNavItems.value.find(i => i.id === activeSection.value);
  return item?.label ?? '';
});

async function handleLogout(): Promise<void> {
  emit('close');
  await auth.logout();
}

// Reset to initial section when opening
watch(
  () => props.visible,
  newVal => {
    if (newVal) {
      activeSection.value = props.initialSection;
    }
  }
);
</script>

<style scoped>
/* Backdrop */
.sh-backdrop {
  position: fixed;
  inset: 0;
  z-index: 8900;
  background: rgba(10, 10, 15, 0.9);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

@media (min-width: 768px) {
  .sh-backdrop {
    align-items: stretch;
    justify-content: flex-end;
  }
}

/* Panel */
.sh-panel {
  background: var(--color-arena-surface);
  border: 2px solid var(--color-arena-border);
  border-bottom: none;
  border-radius: 1rem 1rem 0 0;
  width: 100%;
  /* Fixed height (not max-height) so the panel does not resize when switching
     tabs to content of a different length; content scrolls inside instead.
     dvh accounts for mobile browser chrome. (#290) */
  height: 92dvh;
  max-height: 92dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.5);
}

@media (min-width: 768px) {
  .sh-panel {
    width: 520px;
    max-height: 100vh;
    border: 2px solid var(--color-arena-border);
    border-radius: 0;
    border-left: 2px solid var(--color-arena-border);
    box-shadow: -8px 0 40px rgba(0, 0, 0, 0.5);
  }
}

/* Header */
.sh-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.75rem;
  border-bottom: 1px solid var(--color-arena-border);
  flex-shrink: 0;
}

.sh-title {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--color-energy-primary);
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.sh-close {
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: color 150ms;
  line-height: 1;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sh-close:hover {
  color: var(--color-arena-text-bright);
}

/* Body: sidebar + content on desktop */
.sh-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .sh-body {
    flex-direction: row;
  }
}

/* Desktop sidebar */
.sh-sidebar {
  display: none;
}

@media (min-width: 768px) {
  .sh-sidebar {
    display: flex;
    flex-direction: column;
    width: 160px;
    flex-shrink: 0;
    border-right: 1px solid var(--color-arena-border);
    padding: 0.75rem 0;
    overflow-y: auto;
  }
}

.sh-nav-group {
  padding: 0.375rem 0;
}

.sh-nav-group + .sh-nav-group {
  border-top: 1px solid rgba(61, 52, 40, 0.5);
}

.sh-nav-group-label {
  display: block;
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--color-arena-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  padding: 0.5rem 1rem 0.25rem;
}

.sh-nav-item {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-left: 3px solid transparent;
  color: var(--color-arena-text-muted);
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition:
    color 150ms,
    border-color 150ms,
    background 150ms;
}

.sh-nav-item:hover {
  color: var(--color-arena-text-bright);
  background: rgba(255, 255, 255, 0.02);
}

.sh-nav-item--active {
  color: var(--color-arena-text-bright);
  border-left-color: var(--color-energy-primary);
  background: rgba(201, 168, 76, 0.04);
}

/* Mobile horizontal nav */
.sh-mobile-nav {
  display: flex;
  overflow-x: auto;
  border-bottom: 1px solid var(--color-arena-border);
  flex-shrink: 0;
  padding: 0 0.75rem;
  gap: 0.125rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.sh-mobile-nav::-webkit-scrollbar {
  display: none;
}

@media (min-width: 768px) {
  .sh-mobile-nav {
    display: none;
  }
}

.sh-mobile-nav-item {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--color-arena-text-muted);
  font-family: var(--font-sans);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.625rem 0.5rem;
  white-space: nowrap;
  transition:
    color 150ms,
    border-color 150ms;
  margin-bottom: -1px;
}

.sh-mobile-nav-item:hover {
  color: var(--color-arena-text-bright);
}

.sh-mobile-nav-item--active {
  color: var(--color-arena-text-bright);
  border-bottom-color: var(--color-energy-primary);
}

/* Content area */
.sh-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
}

.sh-section-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-arena-text-bright);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
}

/* Footer */
.sh-footer {
  border-top: 1px solid var(--color-arena-border);
  padding: 0.75rem 1.25rem;
  flex-shrink: 0;
}

.sh-logout {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  background: transparent;
  border: none;
  color: var(--color-arena-text-muted);
  font-family: var(--font-sans);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition:
    color 150ms,
    background 150ms;
}

.sh-logout:hover {
  color: var(--color-arena-text-bright);
  background: rgba(255, 255, 255, 0.03);
}

.sh-logout i {
  font-size: 0.875rem;
}

/* Transitions */
.settings-hub-enter-active,
.settings-hub-leave-active {
  transition: opacity 200ms ease;
}

.settings-hub-enter-active .sh-panel,
.settings-hub-leave-active .sh-panel {
  transition: transform 200ms ease;
}

.settings-hub-enter-from,
.settings-hub-leave-to {
  opacity: 0;
}

/* Mobile: slide up */
.settings-hub-enter-from .sh-panel {
  transform: translateY(20px);
}

.settings-hub-leave-to .sh-panel {
  transform: translateY(20px);
}

/* Desktop: slide from right */
@media (min-width: 768px) {
  .settings-hub-enter-from .sh-panel {
    transform: translateX(20px);
  }

  .settings-hub-leave-to .sh-panel {
    transform: translateX(20px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .settings-hub-enter-active,
  .settings-hub-leave-active {
    transition: none;
  }

  .settings-hub-enter-active .sh-panel,
  .settings-hub-leave-active .sh-panel {
    transition: none;
  }
}
</style>
